import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, Search, BookOpen, AlertTriangle, FileText, LayoutGrid,
  Briefcase, ExternalLink, Filter, Zap, Wrench,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  heating: Zap, flow: Wrench, sensor: AlertTriangle,
  electric: Zap, error: AlertTriangle,
};

const CATEGORY_LABELS = {
  heating: "Chauffage",
  flow: "Débit",
  sensor: "Sondes",
  electric: "Électrique",
  error: "Autres",
};

const SEVERITY_STYLES = {
  critical: "bg-red-100 text-red-700 border-red-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  info: "bg-slate-100 text-slate-700 border-slate-200",
};

const DOC_TYPE_LABELS = {
  manual: "Manuel",
  troubleshoot: "Dépannage",
  exploded_view: "Vue éclatée",
  schematic: "Schéma",
};

export default function Pro() {
  const [tab, setTab] = useState("overview"); // 'overview' | 'error-codes' | 'docs'
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/pro/stats")
      .then(({ data }) => setStats(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12" data-testid="pro-space">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-lg bg-slate-950 text-amber-400">
          <Briefcase className="h-5 w-5" />
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">Espace Pro</p>
          <h1 className="mt-0.5 font-display text-3xl lg:text-4xl font-black tracking-tight">
            DoctoSpa Pro
          </h1>
        </div>
        <span className="ml-auto rounded-full bg-amber-400 text-slate-950 px-3 py-1 text-xs font-bold uppercase tracking-wider">
          Pro
        </span>
      </div>

      <p className="mt-3 text-sm text-muted-foreground max-w-2xl leading-relaxed">
        Documentation technique, vues éclatées et base de codes erreur — l'atelier
        de référence pour les techniciens et installateurs.
      </p>

      {/* Tabs */}
      <div className="mt-8 flex flex-wrap gap-2 border-b border-black/10" data-testid="pro-tabs">
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")} icon={LayoutGrid} testid="tab-overview">
          Vue d'ensemble
        </TabButton>
        <TabButton active={tab === "error-codes"} onClick={() => setTab("error-codes")} icon={AlertTriangle} testid="tab-error-codes">
          Codes erreur
        </TabButton>
        <TabButton active={tab === "docs"} onClick={() => setTab("docs")} icon={BookOpen} testid="tab-docs">
          Documentation & vues éclatées
        </TabButton>
      </div>

      <div className="mt-8">
        {tab === "overview" && <OverviewTab stats={stats} onGo={setTab} />}
        {tab === "error-codes" && <ErrorCodesTab brands={stats?.brands || []} />}
        {tab === "docs" && <DocsTab brands={stats?.brands || []} />}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 font-medium text-sm transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}

function OverviewTab({ stats, onGo }) {
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 border-black/10" data-testid="stat-codes">
          <div className="flex items-center justify-between">
            <div className="eyebrow">Codes erreur</div>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="mt-3 font-display text-4xl font-black">{stats?.error_codes_count ?? 0}</div>
          <p className="mt-1 text-xs text-muted-foreground">enrichi en continu par nos experts</p>
        </Card>
        <Card className="p-6 border-black/10" data-testid="stat-docs">
          <div className="flex items-center justify-between">
            <div className="eyebrow">Documents</div>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="mt-3 font-display text-4xl font-black">{stats?.docs_count ?? 0}</div>
          <p className="mt-1 text-xs text-muted-foreground">manuels, dépannage, vues éclatées</p>
        </Card>
        <Card className="p-6 border-black/10" data-testid="stat-brands">
          <div className="flex items-center justify-between">
            <div className="eyebrow">Marques couvertes</div>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="mt-3 font-display text-4xl font-black">{stats?.brands?.length ?? 0}</div>
          <p className="mt-1 text-xs text-muted-foreground capitalize">
            {(stats?.brands || []).slice(0, 5).join(" · ")}
            {stats?.brands?.length > 5 && "…"}
          </p>
        </Card>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card
          className="p-8 border-black/10 hover:border-primary/30 transition-colors cursor-pointer"
          onClick={() => onGo("error-codes")}
          data-testid="quick-error-codes"
        >
          <AlertTriangle className="h-6 w-6 text-red-600" />
          <h3 className="mt-4 font-display text-xl font-semibold">Chercher un code erreur</h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            FLO, OH, HL, SN, R02… Retrouvez instantanément la signification, la cause probable
            et les actions à mener pour toutes les grandes marques.
          </p>
        </Card>
        <Card
          className="p-8 border-black/10 hover:border-primary/30 transition-colors cursor-pointer"
          onClick={() => onGo("docs")}
          data-testid="quick-docs"
        >
          <BookOpen className="h-6 w-6 text-primary" />
          <h3 className="mt-4 font-display text-xl font-semibold">Documentation & vues éclatées</h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Manuels installateurs, guides de dépannage, schémas hydrauliques et vues éclatées
            des blocs techniques.
          </p>
        </Card>
      </div>
    </div>
  );
}

function ErrorCodesTab({ brands }) {
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (brand) params.set("brand", brand);
      api.get(`/pro/error-codes?${params.toString()}`)
        .then(({ data }) => setItems(data.items || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, brand]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un code (FLO, OH, HL, R02…) ou un mot-clé"
            className="pl-10 h-11"
            data-testid="error-codes-search"
          />
        </div>
        <select
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          data-testid="error-codes-brand-filter"
          className="h-11 rounded-md border border-black/10 bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 capitalize"
        >
          <option value="">Toutes les marques</option>
          {brands.map((b) => (
            <option key={b} value={b} className="capitalize">{b}</option>
          ))}
        </select>
      </div>

      <div className="mt-6" data-testid="error-codes-list">
        {loading ? (
          <div className="py-16 grid place-items-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <Card className="p-10 border-black/10 text-center text-sm text-muted-foreground">
            Aucun code trouvé. Essayez un autre terme.
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {items.map((ec) => (
              <ErrorCodeCard key={ec.id} ec={ec} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ErrorCodeCard({ ec }) {
  const CategoryIcon = CATEGORY_ICONS[ec.category] || AlertTriangle;
  const sevClass = SEVERITY_STYLES[ec.severity] || SEVERITY_STYLES.info;
  return (
    <Card className="p-5 border-black/10" data-testid={`error-code-${ec.code}-${ec.brand}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="rounded-md bg-slate-950 text-amber-400 px-3 py-1.5 text-sm font-black font-mono tracking-wider">
            {ec.code}
          </span>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground capitalize">
              {ec.brand}
            </div>
            <div className="text-sm font-semibold mt-0.5">{ec.title}</div>
          </div>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${sevClass}`}>
          {ec.severity}
        </span>
      </div>
      <p className="mt-3 text-sm text-foreground leading-relaxed">{ec.description}</p>
      <div className="mt-3 rounded-lg bg-primary/5 border border-primary/10 p-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
          <CategoryIcon className="h-3.5 w-3.5" /> {CATEGORY_LABELS[ec.category] || ec.category}
        </div>
        <p className="mt-1.5 text-sm text-foreground leading-relaxed">{ec.action_summary}</p>
      </div>
    </Card>
  );
}

function DocsTab({ brands }) {
  const [brand, setBrand] = useState("");
  const [docType, setDocType] = useState("");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (brand) params.set("brand", brand);
    if (docType) params.set("doc_type", docType);
    api.get(`/pro/docs?${params.toString()}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [brand, docType]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          data-testid="docs-brand-filter"
          className="h-11 rounded-md border border-black/10 bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 capitalize"
        >
          <option value="">Toutes les marques</option>
          {brands.map((b) => (
            <option key={b} value={b} className="capitalize">{b}</option>
          ))}
        </select>
        <select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          data-testid="docs-type-filter"
          className="h-11 rounded-md border border-black/10 bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="">Tous les types</option>
          {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      <div className="mt-6" data-testid="docs-list">
        {loading ? (
          <div className="py-16 grid place-items-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <Card className="p-10 border-black/10 text-center text-sm text-muted-foreground">
            Aucun document trouvé pour ces filtres.
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((d) => <DocCard key={d.id} doc={d} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function DocCard({ doc }) {
  return (
    <Card className="p-5 border-black/10 flex flex-col" data-testid={`doc-${doc.id}`}>
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
          {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground capitalize">
          {doc.brand}{doc.model && ` · ${doc.model}`}
        </span>
      </div>
      <h3 className="mt-3 font-display font-semibold leading-snug">{doc.title}</h3>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{doc.summary}</p>
      {doc.tags?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {doc.tags.map((t) => (
            <span key={t} className="rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[10px]">
              {t}
            </span>
          ))}
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-black/5">
        {doc.external_url ? (
          <a
            href={doc.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            data-testid={`doc-link-${doc.id}`}
          >
            Consulter <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : (
          <span className="text-xs text-muted-foreground italic">
            Fiche bientôt disponible
          </span>
        )}
      </div>
    </Card>
  );
}
